/**
 * Hooks del módulo de marco legal (mejoras N2 y N3 del changelog cliente).
 *
 * - useLegalCatalog: trae el catálogo curado para la página
 *   /configuracion/marco-legal.
 * - useVerifyProposalLegal: lanza la verificación de UNA propuesta.
 * - useCreate/Update/DeleteLegalReference: CRUD de normas custom (N3).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";
import type { components } from "@/lib/api/generated";

export type LegalReference = components["schemas"]["LegalReference"];
export type LegalReferenceCreate =
  components["schemas"]["LegalReferenceCreate"];
export type LegalReferenceUpdate =
  components["schemas"]["LegalReferenceUpdate"];
export type LegalCatalogResponse = components["schemas"]["LegalCatalogResponse"];
export type DetectedCitation = components["schemas"]["DetectedCitation"];
export type LegalVerificationResult =
  components["schemas"]["LegalVerificationResult"];
export type LegalVerificationSummary =
  components["schemas"]["LegalVerificationSummary"];
export type LegalFrameworkItem = components["schemas"]["LegalFrameworkItem"];
export type LegalFrameworkResponse =
  components["schemas"]["LegalFrameworkResponse"];

async function fetchLegalCatalog(): Promise<LegalCatalogResponse> {
  return fetchWithAuth("/api/v1/legal/catalog");
}

async function verifyProposalLegal(
  proposalId: string
): Promise<LegalVerificationResult> {
  return fetchWithAuth(
    `/api/v1/legal/verify-proposal/${proposalId}`,
    { method: "POST", body: JSON.stringify({}) }
  );
}

/**
 * Catálogo curado de normativa. Stale time alto porque cambia muy poco
 * (solo cuando alguien actualiza el YAML y se redeploya).
 */
export function useLegalCatalog() {
  return useQuery<LegalCatalogResponse, Error>({
    queryKey: ["legal-catalog"],
    queryFn: fetchLegalCatalog,
    staleTime: 30 * 60 * 1000, // 30 min
  });
}

/**
 * Verifica TODAS las secciones de una propuesta. Como cada llamada
 * cuesta tokens, no auto-invalida cache: el usuario decide cuándo
 * relanzar pulsando el botón otra vez.
 */
export function useVerifyProposalLegal() {
  return useMutation<LegalVerificationResult, Error, { proposalId: string }>({
    mutationFn: ({ proposalId }) => verifyProposalLegal(proposalId),
    onError: (error) => {
      showToast.error(
        error.message || "Error al verificar el marco legal de la propuesta"
      );
    },
  });
}

// ============ CRUD de normas custom (mejora N3) ============

export function useCreateLegalReference() {
  const queryClient = useQueryClient();
  return useMutation<LegalReference, Error, LegalReferenceCreate>({
    mutationFn: (payload) =>
      fetchWithAuth("/api/v1/legal/catalog/custom", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["legal-catalog"] });
      showToast.success("Norma añadida al catálogo");
    },
    onError: (error) => {
      showToast.error(error.message || "Error al crear la norma");
    },
  });
}

export function useUpdateLegalReference() {
  const queryClient = useQueryClient();
  return useMutation<
    LegalReference,
    Error,
    { slug: string; data: LegalReferenceUpdate }
  >({
    mutationFn: ({ slug, data }) =>
      fetchWithAuth(`/api/v1/legal/catalog/custom/${slug}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["legal-catalog"] });
      showToast.success("Norma actualizada");
    },
    onError: (error) => {
      showToast.error(error.message || "Error al actualizar la norma");
    },
  });
}

export function useDeleteLegalReference() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (slug) =>
      fetchWithAuth(`/api/v1/legal/catalog/custom/${slug}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["legal-catalog"] });
      showToast.success("Norma eliminada del catálogo");
    },
    onError: (error) => {
      showToast.error(error.message || "Error al eliminar la norma");
    },
  });
}

// ============ Marco legal por licitación ============

/**
 * Marco legal aplicable a UNA licitación: subconjunto del catálogo global.
 * Incluye el núcleo (base, aplica siempre) + las selecciones (ia/manual).
 */
export function useLegalFramework(licitacionId: string | undefined) {
  return useQuery<LegalFrameworkResponse, Error>({
    queryKey: ["legal-framework", licitacionId],
    queryFn: () =>
      fetchWithAuth(`/api/v1/licitaciones/${licitacionId}/legal-framework`),
    enabled: !!licitacionId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Sugiere con IA, desde el pliego, las normas aplicables. Cuesta tokens, así
 * que es una mutación explícita (la dispara el usuario). Persiste como 'ia'.
 */
export function useSuggestLegalFramework(licitacionId: string) {
  const queryClient = useQueryClient();
  return useMutation<LegalFrameworkResponse, Error, void>({
    mutationFn: () =>
      fetchWithAuth(
        `/api/v1/licitaciones/${licitacionId}/legal-framework/suggest`,
        { method: "POST", body: JSON.stringify({}) }
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(["legal-framework", licitacionId], data);
      showToast.success(data.notice || "Marco legal sugerido desde el pliego");
    },
    onError: (error) => {
      showToast.error(error.message || "Error al sugerir el marco legal");
    },
  });
}

/** Añade una norma del catálogo al marco de la licitación. */
export function useAddLegalFrameworkNorm(licitacionId: string) {
  const queryClient = useQueryClient();
  return useMutation<LegalFrameworkResponse, Error, string>({
    mutationFn: (slug) =>
      fetchWithAuth(`/api/v1/licitaciones/${licitacionId}/legal-framework`, {
        method: "POST",
        body: JSON.stringify({ slug }),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["legal-framework", licitacionId], data);
    },
    onError: (error) => {
      showToast.error(error.message || "Error al añadir la norma");
    },
  });
}

/** Quita una norma (no base) del marco de la licitación. */
export function useRemoveLegalFrameworkNorm(licitacionId: string) {
  const queryClient = useQueryClient();
  return useMutation<LegalFrameworkResponse, Error, string>({
    mutationFn: (slug) =>
      fetchWithAuth(
        `/api/v1/licitaciones/${licitacionId}/legal-framework/${slug}`,
        { method: "DELETE" }
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(["legal-framework", licitacionId], data);
    },
    onError: (error) => {
      showToast.error(error.message || "Error al quitar la norma");
    },
  });
}

/**
 * Oculta una norma BUILT-IN (predefinida) del catálogo. El YAML es
 * read-only, así que en lugar de borrarla la marcamos como oculta; deja
 * de aparecer en el catálogo y en el verificador. Reversible.
 */
export function useHideBuiltinLegalReference() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (slug) =>
      fetchWithAuth(`/api/v1/legal/catalog/builtin/${slug}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["legal-catalog"] });
      showToast.success("Norma ocultada del catálogo");
    },
    onError: (error) => {
      showToast.error(error.message || "Error al ocultar la norma");
    },
  });
}
